<template>
  <v-form
    ref="form"
    v-model="valid"
    class="draft-form"
    @submit.prevent="submitForm"
  >
    <div class="d-flex flex-column ga-4">
      <DpplTextField
        v-model="title"
        label="Title"
        outlined
        :rules="titleRules"
        :disabled="loading"
        dense
      />
      <DpplTextField
        v-model="location"
        label="Location"
        outlined
        :rules="locationRules"
        :disabled="loading"
        dense
      />
      <DpplTextField
        v-model="flexibleId"
        label="Job ID (Optional)"
        outlined
        :rules="flexibleIdRules"
        :disabled="loading"
        dense
      />
      <DpplSelect
        v-model="template"
        :search-select="true"
        :select-on-open-menu="true"
        :disabled="loading"
        data-testid="template-select"
        :items="templatesWithDefaultLabeled"
        item-title="title"
        item-value="id"
        item-color="primary"
        label="Template"
        variant="outlined"
        placeholder="Select Template"
      />
      <v-btn
        color="primary"
        variant="flat"
        rounded="lg"
        type="submit"
        :loading="loading && !firstDraftLoading"
        :disabled="!valid || firstDraftLoading"
      >
        Create Draft
      </v-btn>
      <FirstDraftButton
        v-if="aiEnabled"
        class="mr-4 w-100"
        :disabled="!valid || loading"
        :loading="firstDraftLoading"
        @click="onFirstDraftClick"
      >
        Create with First Draft
      </FirstDraftButton>
    </div>
  </v-form>
</template>

<script>
import { mapActions } from 'pinia';
import { recruitJsonCreate, recruitJsonGetTemplates } from '@/api/recruitJson';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useTracking } from '@/stores/tracking/tracking';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplSelect from '@/components/datapeople/DpplSelect.vue';
import FirstDraftButton from '@ui/components/firstDraft/FirstDraftButton.vue';
import validation from '@/helpers/validation';

export default {
  name: 'CreateDraftForm',

  components: {
    DpplTextField,
    DpplSelect,
    FirstDraftButton,
  },

  emits: ['created', 'cancel', 'template-changed'],

  data() {
    return {
      title: '',
      location: '',
      template: null,
      flexibleId: null,
      valid: false,
      templateList: [],
      titleRules: validation.titleRules(),
      locationRules: validation.locationRules(),
      flexibleIdRules: [
        (v) => v === null || v === '' || (v && v.length <= 250) || 'Job ID is too long',
      ],
      loading: false,
      firstDraftLoading: false,
    };
  },

  computed: {
    templatesWithDefaultLabeled() {
      const templateCopy = this.templateList.map((template) => ({ ...template }));
      const defaultIndex = templateCopy.findIndex(({ is_default: isDefault }) => isDefault);
      if (defaultIndex !== -1) {
        templateCopy[defaultIndex].title = `${templateCopy[defaultIndex].title} (Default)`;
      }
      return templateCopy;
    },
    defaultTemplateId() {
      if (this.templatesWithDefaultLabeled.length > 0) {
        return this.templatesWithDefaultLabeled.find(({ is_default: isDefault }) => isDefault)
          ? this.templatesWithDefaultLabeled.find(({ is_default: isDefault }) => isDefault).id
          : this.templatesWithDefaultLabeled[0].id;
      }
      return null;
    },
    aiEnabled() {
      return useAppAuthData().authData.aiEnabled;
    },
  },

  watch: {
    template(val) {
      this.$emit('template-changed', val);
    },
  },

  async mounted() {
    await this.fetchTemplates();
    this.resetForm();
  },

  methods: {
    ...mapActions(useAppSnackbar, ['addErrorSnackbar', 'addMultilineErrorSnackbar']),
    async fetchTemplates() {
      try {
        this.loading = true;
        const { templates } = await recruitJsonGetTemplates();
        this.templateList = templates;
      } catch (e) {
        this.addErrorSnackbar({
          text: 'Error loading templates.',
        });
        throw e;
      } finally {
        this.loading = false;
      }
    },
    async submitForm({ isFirstDraft } = {}) {
      try {
        const formValidateResult = await this.$refs.form.validate();
        if (formValidateResult.valid) {
          this.loading = true;
          const responseJSON = await recruitJsonCreate({
            title: this.title,
            location: this.location,
            template: this.template,
            isPrivate: false,
            flexibleId: this.flexibleId,
          });
          this.$emit('created', { ...responseJSON, isFirstDraft });
        }
      } catch (e) {
        this.addMultilineErrorSnackbar({
          text: `An error occurred while creating this draft.
            Our support team will contact you once the issue has been resolved.
            ${e.message}
          `,
        });
        throw e;
      } finally {
        this.loading = false;
      }
    },
    async onFirstDraftClick() {
      try {
        this.firstDraftLoading = true;

        useTracking().trackName({ name: 'Clicked: Write with First Draft' });

        await this.submitForm({ isFirstDraft: true });
      } finally {
        this.firstDraftLoading = false;
      }
    },
    resetForm() {
      this.template = this.defaultTemplateId;
      this.title = '';
      this.location = '';
      this.isPublic = true;
      if (this.flexibleId) {
        this.flexibleId = '';
      }
      this.$refs.form.resetValidation();
    },
  },

};
</script>
